import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { LoginButton } from "./LoginButton";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAdminLogin, useUserLogin, MAX_LOGIN_ATTEMPTS } from "../api/login";
import { TryLoginMessage } from "./TryLoginMessage";
import { StyledTextField } from "./StyledTextField";
import { LoadingOverlay } from "./LoadingOverlay";
import { BaseContext } from "..";

const useSimpleInput = (initialValue: string) => {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
  }, [value]);

  return {
    value,
    setValue: (value?: string) => setValue(value ?? ""),
    error,
    setError,
  };
};

type UseUserLoginType = ReturnType<typeof useUserLogin>;
type LoginFormProps = {
  onChangeLogin: () => void;
  login: (email: string, password: string) => ReturnType<UseUserLoginType>;
  text: string;
  // localStorage 名前空間(受講者/管理者で分ける)。
  storageKey: string;
};

// --- ログイン試行回数の表示を localStorage に保持する -------------------------
// サーバが実際のロックを保証する(エンフォースの本体)。ここで保存するのは「表示」だけで、
// ブラウザを更新しても残り回数・ロック表示が 5/5 に戻らないようにするためのもの。
// 失効時刻 expiresAt はサーバの失敗カウンタ窓(reset_in)に同期させているので、
// 窓が切れれば表示も自動的に満タンへ戻る。
type PersistedAttempts = { remaining: number; expiresAt: number };

const attemptsStorageKey = (key: string) => `login_attempts:${key}`;

const readPersistedAttempts = (
  key: string
): { remaining: number; lockSeconds: number } => {
  const fresh = { remaining: MAX_LOGIN_ATTEMPTS, lockSeconds: 0 };
  try {
    const raw = localStorage.getItem(attemptsStorageKey(key));
    if (!raw) return fresh;
    const parsed = JSON.parse(raw) as PersistedAttempts;
    const msLeft = parsed.expiresAt - Date.now();
    if (
      typeof parsed.remaining !== "number" ||
      typeof parsed.expiresAt !== "number" ||
      msLeft <= 0
    ) {
      // 窓が切れている/壊れている → 破棄して満タン表示に戻す
      localStorage.removeItem(attemptsStorageKey(key));
      return fresh;
    }
    return {
      remaining: parsed.remaining,
      // ロック中(残り 0)のときだけ、更新後もカウントダウンを継続する
      lockSeconds: parsed.remaining <= 0 ? Math.ceil(msLeft / 1000) : 0,
    };
  } catch {
    return fresh;
  }
};

const writePersistedAttempts = (
  key: string,
  remaining: number,
  resetIn: number,
  max: number
) => {
  try {
    // 満タン or 窓情報なしのときは保存しない(=次回更新時は満タン表示)。
    // 「満タン」の判定はサーバ由来の上限(max)で行う(MAX_LOGIN_ATTEMPTS はブートストラップ値)。
    if (remaining >= max || resetIn <= 0) {
      localStorage.removeItem(attemptsStorageKey(key));
      return;
    }
    const value: PersistedAttempts = {
      remaining,
      expiresAt: Date.now() + resetIn * 1000,
    };
    localStorage.setItem(attemptsStorageKey(key), JSON.stringify(value));
  } catch {
    // localStorage 不可(プライベートモード等)でも機能は継続する
  }
};

const clearPersistedAttempts = (key: string) => {
  try {
    localStorage.removeItem(attemptsStorageKey(key));
  } catch {
    // no-op
  }
};

export const UserLoginForm: React.FC<Pick<LoginFormProps, "onChangeLogin">> = ({
  onChangeLogin,
}) => {
  const setSession = useContext(BaseContext);
  const userLogin = useUserLogin(setSession!);

  const text = "管理者はこちら";
  return (
    <LoginForm
      onChangeLogin={onChangeLogin}
      login={userLogin}
      text={text}
      storageKey="user"
    />
  );
};

export const AdminLoginForm: React.FC<
  Pick<LoginFormProps, "onChangeLogin">
> = ({ onChangeLogin }) => {
  const setSession = useContext(BaseContext);
  const adminLogin = useAdminLogin(setSession!);

  const text = "受講者はこちら";
  return (
    <LoginForm
      onChangeLogin={onChangeLogin}
      login={adminLogin}
      text={text}
      storageKey="admin"
    />
  );
};

const LoginForm: React.FC<LoginFormProps> = ({
  onChangeLogin,
  login,
  text,
  storageKey,
}) => {
  const email = useSimpleInput("");
  const password = useSimpleInput("");
  const [showPassword, setShowPassword] = useState(false);
  // 残り試行回数・ロック秒数はサーバ(権威)の値で駆動する。ローカル state は表示用にすぎないが、
  // localStorage から初期化することで、更新後も残り回数・ロック表示が 5/5 に戻らないようにする。
  // (サーバ側のカウントは元々回復しないため、上限到達後は確実に弾かれる。)
  const persisted = useMemo(
    () => readPersistedAttempts(storageKey),
    [storageKey]
  );
  const [remaining, setRemaining] = useState(persisted.remaining);
  // 上限(分母)もサーバ値を権威とする。初回はブートストラップ値で描画し、最初の応答で上書きする。
  const [maxAttempts, setMaxAttempts] = useState(MAX_LOGIN_ATTEMPTS);
  const [lockSeconds, setLockSeconds] = useState(persisted.lockSeconds);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const disabled = lockSeconds > 0;

  // ロック中はサーバ由来の reset_in を 1 秒ごとにカウントダウンする。
  useEffect(() => {
    if (lockSeconds <= 0) return;
    const timer = setTimeout(
      () => setLockSeconds((s) => Math.max(0, s - 1)),
      1000
    );
    return () => clearTimeout(timer);
  }, [lockSeconds]);

  // ロック → 解除に変わった瞬間だけ、残り回数表示を満タンに戻し、保存も破棄する
  // (この頃にはサーバ側の窓もリセットされ、次の試行で実際の残り回数が返る)。
  const wasLocked = useRef(false);
  useEffect(() => {
    if (lockSeconds > 0) {
      wasLocked.current = true;
    } else if (wasLocked.current) {
      wasLocked.current = false;
      setRemaining(maxAttempts);
      clearPersistedAttempts(storageKey);
    }
  }, [lockSeconds, storageKey, maxAttempts]);

  const onLogin = useCallback(async () => {
    // 認証リクエスト中は「ログイン中...」を表示。
    // バックエンド(Cloud Run)のコールドスタート待ちがこのリクエスト中に発生するため、
    // ここでフィードバックを出すことで無反応に見える時間をなくす。
    setIsLoggingIn(true);
    try {
      const outcome = await login(email.value, password.value);
      if (outcome.errors.length === 0 && !outcome.locked) {
        // 認証成功。setSession により Router がログイン後の画面へ切り替わる(本フォームはアンマウントされる)。
        // オーバーレイは表示したままにして、画面切り替わりまでの間も「ログイン中...」を維持する。
        clearPersistedAttempts(storageKey);
        return;
      }
      outcome.errors.forEach((err) => {
        if (err.type === "email") {
          email.setError(err.message);
        } else if (err.type === "password") {
          password.setError(err.message);
        }
      });
      // サーバが上限を返したら表示(分母)を更新。応答が来れば常にサーバ値が権威。
      const max = outcome.maxAttempts ?? maxAttempts;
      if (outcome.maxAttempts !== null) {
        setMaxAttempts(outcome.maxAttempts);
      }
      // サーバが残り回数を返したときだけ表示を更新・保存(バリデーション/通信エラーでは消費しない)。
      if (outcome.remainingAttempts !== null) {
        setRemaining(outcome.remainingAttempts);
        writePersistedAttempts(
          storageKey,
          outcome.remainingAttempts,
          outcome.resetIn,
          max
        );
      }
      if (outcome.locked) {
        // ロック秒数は常にサーバの reset_in を使う(固定フォールバックは廃止)。
        setLockSeconds(outcome.resetIn);
      }
      setIsLoggingIn(false);
    } catch (e) {
      // ネットワークエラー等。ここではオーバーレイを閉じる。
      console.error(e);
      setIsLoggingIn(false);
    }
  }, [email, password, login, storageKey, maxAttempts]);

  return (
    <>
      <StyledTextField
        label="メールアドレス"
        variant="outlined"
        value={email.value}
        onChange={(e) => {
          email.setValue(e.target.value);
        }}
        required
        error={email.error !== null}
        helperText={email.error}
        disabled={disabled}
        InputProps={{
          style: { backgroundColor: "white" },
        }}
      />
      <StyledTextField
        label="パスワード"
        type={showPassword ? "text" : "password"}
        variant="outlined"
        value={password.value}
        onChange={(e) => {
          password.setValue(e.target.value);
        }}
        required
        error={password.error !== null}
        helperText={password.error}
        InputProps={{
          style: { backgroundColor: "white" },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => setShowPassword(!showPassword)}
                // onMouseDown={handleMouseDownPassword}
              >
                {showPassword ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
        disabled={disabled}
      />
      <Button
        onClick={onChangeLogin}
        sx={{
          width: "35%",
          alignSelf: "flex-end",
          textDecoration: "underline",
          "&:hover": {
            transform: "scale(1.025)",
          },
        }}
      >
        {text}
      </Button>
      <TryLoginMessage
        remaining={remaining}
        maxAttempts={maxAttempts}
        lockSeconds={lockSeconds}
      />
      <LoginButton disabled={disabled || isLoggingIn} onClick={onLogin} />
      <LoadingOverlay open={isLoggingIn} message="ログイン中..." />
    </>
  );
};
